import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { CustomTextField } from "../CustomFields/TextField";
import { LoadingButton } from "@mui/lab";
import {
  FormProvider,
  useForm
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import SkeletonWrapper from "../Skeleton/SkeletonWrapper";
import { PreferencesContext } from "../../PreferenceContext";
import { AlertType } from "../../services/models/AlertType";
import { SendMultipleFilesToEmail } from "../../services/EmailSendService";
import { CustomSelectWithoutForm } from "../CustomFields/CustomSelect";
import {
  GetNotificationTemplateList,
  UpdateDefaultNotificationTemplate
} from "../../services/NotificationTemplateService";
import { GetOpdMessagePreview } from "../../services/OPDService";
import { UI_APP_URL } from "../../services/common/const";
import parse from "html-react-parser";
import html2pdf from "html2pdf.js";
import { GetOpdDetailsForReport } from "../../services/ReportService";
import { GetPatientReportCertificatePreview } from "../../services/CertificateService";
import ReportCertificateHeader from "../../pages/OPD/Certificates/ReportCertificateHeader";
import ReportCertificateFooter from "../../pages/OPD/Certificates/ReportCertificateFooter";
import { GetOPDInvestigationDetailById } from "../../services/OPDInvestigationService";
import { GetGrowthChart } from "../../services/models/GrowthChart";
import { GetFullPrescriptionReportDetail, GetFullPrescriptionTokenDetail } from "../../services/TokenService";
import { GetRedirectLink } from "../../services/ShortLinkService";
import PriscriptionFooter from "../../pages/Report/Priscription/PriscriptionFooter";
import PriscriptionHeader from "../../pages/Report/Priscription/PriscriptionHeader";
import PatientDetails from "../../pages/Report/Priscription/PatientDetails";
import PriscriptionContent from "../../pages/Report/Priscription/PriscriptionContent";
import HeightChart from "../Chart/HeightChart";
import WeightChart from "../Chart/WeightChart";
import BMIChart from "../Chart/BMIChart";
import HeadCircumferenceChart from "../Chart/HeadCircumferenceChart";

const availableLinks = [
  { label: "Prescription", value: "PRESCRIPTION_LINK" },
  { label: "Report Certificate", value: "REPORT_CERTIFICATE_LINK" },
  { label: "Growth Chart", value: "GROWTH_CHART_LINK" },
  { label: "Full Prescription", value: "FULL_PRESCRIPTION_LINK" },
  { label: "Billing", value: "BILLING_LINK" },
];

const enablePdfHeaderFooter = (reportData: any) => ({
  ...reportData,
  businessInformation: { ...reportData?.businessInformation, headerEnable: true, footerEnable: true },
  medicalReportCertificateHeader: { ...reportData?.medicalReportCertificateHeader, headerEnable: true, footerEnable: true },
});

const PrescriptionReportEmailContent = ({
  reportData,
  selectedSections,
}: {
  reportData: any;
  selectedSections: { [key: string]: boolean };
}) => (
  <Box
    className="print-container"
    sx={{
      width: "190mm",
      maxWidth: "190mm",
      boxSizing: "border-box",
      backgroundColor: "#fff",
    }}
  >
    <Box className="pdf-repeat-header">
      {enablePdfHeaderFooter(reportData)?.businessInformation?.headerEnable && (
        <Box className="header">
          <PriscriptionHeader headerData={enablePdfHeaderFooter(reportData).businessInformation} />
        </Box>
      )}
      <Box className="patient-details-section">
        <PatientDetails patientData={reportData} />
      </Box>
    </Box>

    <Box className="content">
      <PriscriptionContent opdContent={reportData} selectedSections={selectedSections} />
    </Box>

    <Box className="pdf-repeat-footer">
      <Box className="footer">
        <PriscriptionFooter footerData={enablePdfHeaderFooter(reportData)} selectedSections={{ ...selectedSections, FOOTER: true }} />
      </Box>
    </Box>
  </Box>
);

const getGrowthCharts = (chartData: any) => {
  const growthSeries = chartData?.growthChartSeriesColl ?? [];
  return [
    {
      key: "height",
      hasData: growthSeries.some((item: any) => item?.height > 0) && chartData?.heightChartZoneColl?.length > 0,
    },
    {
      key: "weight",
      hasData: growthSeries.some((item: any) => item?.weight > 0) && chartData?.weightChartZoneColl?.length > 0,
    },
    {
      key: "bmi",
      hasData: growthSeries.some((item: any) => item?.bmi > 0) && chartData?.bmiChartZoneColl?.length > 0,
    },
    {
      key: "head",
      hasData: growthSeries.some((item: any) => item?.headCircumference > 0) && chartData?.hcChartZoneColl?.length > 0,
    },
  ].filter((chart) => chart.hasData);
};

const GrowthChartEmailContent = ({ chartData, reportData }: { chartData: any; reportData: any }) => {
  const growthSeries = chartData?.growthChartSeriesColl ?? [];
  const charts = getGrowthCharts(chartData);

  if (charts.length === 0) return null;

  return (
    <Box
      sx={{
        width: "190mm",
        maxWidth: "190mm",
        boxSizing: "border-box",
        backgroundColor: "#fff",
      }}
    >
      {charts.map((chart, index) => (
        <Box
          key={chart.key}
          className="growth-chart-page"
          sx={{
            ...(index > 0 && {
              pageBreakBefore: "always",
              breakBefore: "page",
            }),
            pageBreakInside: "avoid",
            breakInside: "avoid",
            marginBottom: 0,
          }}
        >
          {enablePdfHeaderFooter(reportData)?.businessInformation?.headerEnable && (
            <Box className="header">
              <PriscriptionHeader headerData={enablePdfHeaderFooter(reportData).businessInformation} />
            </Box>
          )}
          <Box className="patient-details-section">
            <PatientDetails patientData={reportData} />
          </Box>

          {chart.key === "height" && (
            <HeightChart motherHeight={growthSeries[0]?.motherHeight ?? 0} fatherHeight={growthSeries[0]?.fatherHeight ?? 0} heightList={growthSeries} intervalType={growthSeries[0]?.intervalType ?? "YEAR"} heightChartZoneData={chartData?.heightChartZoneColl} isMobileScreen={false} isDownload />
          )}
          {chart.key === "weight" && (
            <WeightChart weightList={growthSeries} intervalType={growthSeries[0]?.intervalType ?? "YEAR"} weightChartZoneData={chartData?.weightChartZoneColl} isMobileScreen={false} isDownload />
          )}
          {chart.key === "bmi" && (
            <BMIChart bmiList={growthSeries} intervalType={growthSeries[0]?.intervalType ?? "YEAR"} bmiChartZoneData={chartData?.bmiChartZoneColl} isMobileScreen={false} isDownload />
          )}
          {chart.key === "head" && (
            <HeadCircumferenceChart headCircumferenceList={growthSeries} intervalType={growthSeries[0]?.intervalType ?? "YEAR"} headCircumferenceChartZoneData={chartData?.hcChartZoneColl} isMobileScreen={false} isDownload />
          )}
          <Box className="footer">
            <PriscriptionFooter footerData={enablePdfHeaderFooter(reportData)} selectedSections={{ FOOTER: true }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const FullPrescriptionReportEmailContent = ({ reportData }: { reportData: any }) => {
  const growthSeries = reportData?.growthChartSeriesColl ?? [];
  const hasHeight = growthSeries.some((item: any) => item?.height > 0);
  const hasWeight = growthSeries.some((item: any) => item?.weight > 0);
  const hasBMI = growthSeries.some((item: any) => item?.bmi > 0);
  const hasHead = growthSeries.some((item: any) => item?.headCircumference > 0);

  return (
    <Box
      className="print-container"
      sx={{
        width: "190mm",
        maxWidth: "190mm",
        boxSizing: "border-box",
        backgroundColor: "#fff"
      }}
    >
      {/* REPEATING HEADER */}
      <Box
        className="pdf-repeat-header"
        sx={{ width: "794px", maxWidth: "794px", boxSizing: "border-box", backgroundColor: "#fff" }}
      >
        {enablePdfHeaderFooter(reportData)?.businessInformation?.headerEnable && (
          <Box className="header">
            <PriscriptionHeader headerData={enablePdfHeaderFooter(reportData).businessInformation} />
          </Box>
        )}
        <Box className="patient-details-section">
          <PatientDetails patientData={reportData} />
        </Box>
      </Box>

      <Box className="content">
        <PriscriptionContent
          opdContent={reportData}
          selectedSections={{
            VITALS_AND_ANTHROPOMETRY: true,
            CHIEF_COMPLAINTS: true,
            HISTORY: true,
            EXAMINATION: true,
            VACCINATION: true,
            INVESTIGATION: true,
            DIAGNOSIS: true,
            PRESCRIPTION: true,
            FOLLOWUP: true,
            DOCTOR_TO_BE_REFERRED: true,
          }}
        />
      </Box>
      <Box sx={{ width: "100%" }}>
        {hasHeight && (
          <Box className="full-prescription-chart-page" sx={{ marginBottom: "20px", breakInside: "avoid", pageBreakInside: "avoid" }}><HeightChart motherHeight={growthSeries[0]?.motherHeight ?? 0} fatherHeight={growthSeries[0]?.fatherHeight ?? 0} heightList={growthSeries} intervalType={growthSeries[0]?.intervalType ?? "YEAR"} heightChartZoneData={reportData?.heightChartZoneColl} isMobileScreen={false} isDownload />
          </Box>
        )}
        {hasWeight && (
          <Box className="full-prescription-chart-page" sx={{ marginBottom: "20px", breakInside: "avoid", pageBreakInside: "avoid" }}><WeightChart weightList={growthSeries} intervalType={growthSeries[0]?.intervalType ?? "YEAR"} weightChartZoneData={reportData?.weightChartZoneColl} isMobileScreen={false} isDownload />
          </Box>
        )}
        {hasBMI && (
          <Box className="full-prescription-chart-page" sx={{ marginBottom: "20px", breakInside: "avoid", pageBreakInside: "avoid" }}><BMIChart bmiList={growthSeries} intervalType={growthSeries[0]?.intervalType ?? "YEAR"} bmiChartZoneData={reportData?.bmiChartZoneColl} isMobileScreen={false} isDownload />
          </Box>
        )}
        {hasHead && (
          <Box className="full-prescription-chart-page" sx={{ marginBottom: "20px", breakInside: "avoid", pageBreakInside: "avoid" }}>
            <HeadCircumferenceChart headCircumferenceList={growthSeries} intervalType={growthSeries[0]?.intervalType ?? "YEAR"} headCircumferenceChartZoneData={reportData?.hcChartZoneColl} isMobileScreen={false} isDownload />
          </Box>
        )}
      </Box>
      {reportData?.reportCertificateColl?.length > 0 && (
        <Box sx={{ width: "100%", marginTop: "20px", breakInside: "avoid", pageBreakInside: "avoid" }}>
          {reportData.reportCertificateColl.map((cert: any, index: number) => (
            <React.Fragment key={cert.reportCertificateId ?? index}>
              {index > 0 && <Divider sx={{ my: 2, borderColor: "#c7c5c5", borderBottomWidth: "1.5px" }} />}
              <Box
                className="tiptap-editor-preview"
                sx={{
                  margin: "0 16px 10px",
                  lineHeight: 1.5,
                  "& p, & li, & td, & th, & div, & span": { lineHeight: "1.5 !important" },
                  "& table": { borderCollapse: "collapse", tableLayout: "fixed", width: "100% !important", minWidth: "100% !important", maxWidth: "100% !important", margin: "0 auto" },
                }}
                dangerouslySetInnerHTML={{ __html: cert.description || "" }}
              />
            </React.Fragment>
          ))}
        </Box>
      )}
      <Box
        className="pdf-repeat-footer"
        sx={{ width: "794px", maxWidth: "794px", boxSizing: "border-box", backgroundColor: "#fff" }}
      >
        <Box className="footer">
          <PriscriptionFooter footerData={enablePdfHeaderFooter(reportData)} selectedSections={{ FOOTER: true }} />
        </Box>
      </Box>
    </Box>
  );
};

const EmailSendDialogForOPD = (props: any) => {
  // PROPS
  const { onClose, opd } = props;

  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [showCC, setShowCC] = useState(false);
  const [values, setValues] = useState({ to: "", cc: "" });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sendTo, setSendTo] = useState<any>(null);
  const [patientTemplates, setPatientTemplates] = useState<any[]>([]);
  const [referralTemplates, setReferralTemplates] = useState<any[]>([]);
  const [outreachTemplates, setOutreachTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | "">("");
  const [templatePreview, setTemplatePreview] = useState<string>("");
  const [defaultPatientTemplateId, setDefaultPatientTemplateId] = useState<number | "">("");
  const [defaultReferralTemplateId, setDefaultReferralTemplateId] = useState<number | "">("");
  const [defaultOutreachTemplateId, setDefaultOutreachTemplateId] = useState<number | "">("");

  const [selectedLinks, setSelectedLinks] = useState<string[]>([]);
  const [prescriptionPdfData, setPrescriptionPdfData] = useState<any>(null);
  const [certificatePdfData, setCertificatePdfData] = useState<any>(null);
  const [growthChartPdfData, setGrowthChartPdfData] = useState<any>(null);
  const [isFullPrescriptionPdf, setIsFullPrescriptionPdf] = useState(false);
  const prescriptionPdfRef = useRef<HTMLDivElement | null>(null);
  const certificatePdfRef = useRef<HTMLDivElement | null>(null);
  const growthChartPdfRef = useRef<HTMLDivElement | null>(null);

  const sendToOptions = [
    {
      label: "Send to Patient",
      value: "patient",
      email: opd?.patientEmail
    },
    {
      label: "Send to Referral Doctor",
      value: "referral",
      email: opd?.referralDoctorEmail
    },
    {
      label: "Send to Out Reach",
      value: "outreach",
      email: opd?.outReachEmail
    },
  ];

  // SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    to: Yup.string()
      .transform((originalValue) => {
        return originalValue.replace(/\s/g, '').trim();
      })
      .test('is-valid-emails', 'Invalid email addresses.', function (value) {
        if (!value) return true;
        const emailAddresses = value.split(',');
        for (const email of emailAddresses) {
          if (!Yup.string().email().isValidSync(email)) return false;
        }
        return true;
      })
      .required("To is required."),
    cc: Yup.string().email("Invalid email address."),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema),
  });

  const getReportSections = (moduleName: string) =>
    (userContext?.systemConfigurationDetailColl || [])
      .filter((item: any) => item?.configModule === moduleName)
      .reduce((sections: any, item: any) => {
        sections[item.configKey] = item.configValue === "1";
        return sections;
      }, {});

  const waitForPdfContent = () =>
    new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

  const createPdfBase64 = async (root: HTMLDivElement | null) => {
    if (!root) throw new Error("PDF content was not rendered.");

    const PAGE_H = 297; // A4 height (mm)
    const CONTENT_W = 190; // 210 - 10 - 10
    const SIDE = 10;
    const EDGE = 10; // top/bottom outer margin
    const GAP = 3;   // gap between header/footer and body

    const headerEls = Array.from(root.querySelectorAll<HTMLElement>(".pdf-repeat-header"));
    const footerEls = Array.from(root.querySelectorAll<HTMLElement>(".pdf-repeat-footer"));
    const headerEl = headerEls[0] || null;
    const footerEl = footerEls[0] || null;
    const rootRect = root.getBoundingClientRect();

    // Capture header/footer as image and calculate mm size/position
    const capture = async (el: HTMLElement | null) => {
      if (!el || el.offsetHeight === 0 || el.offsetWidth === 0) return null;
      const rect = el.getBoundingClientRect();
      const canvas: HTMLCanvasElement = await html2pdf()
        .set({ html2canvas: { scale: 2, useCORS: true, scrollY: 0 } })
        .from(el)
        .toCanvas()
        .get("canvas");
      const wMm = (rect.width / rootRect.width) * CONTENT_W;
      const hMm = (wMm * canvas.height) / canvas.width;
      const xMm = SIDE + ((rect.left - rootRect.left) / rootRect.width) * CONTENT_W;
      return { img: canvas.toDataURL("image/jpeg", 0.98), wMm, hMm, xMm };
    };

    const header = await capture(headerEl);
    const footer = await capture(footerEl);

    // Hide header/footer while rendering the body so they don't appear twice
    const previousHeaderDisplays = headerEls.map((element) => element.style.display);
    const previousFooterDisplays = footerEls.map((element) => element.style.display);
    if (header) headerEls.forEach((element) => { element.style.display = "none"; });
    if (footer) footerEls.forEach((element) => { element.style.display = "none"; });

    try {
      const topMargin = EDGE + (header ? header.hMm + GAP : 0);
      const bottomMargin = EDGE + (footer ? footer.hMm + GAP : 0);

      const dataUri: string = await html2pdf()
        .set({
          margin: [topMargin, SIDE, bottomMargin, SIDE],
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"], before: ".certificate-pdf-page" },
        })
        .from(root)
        .toPdf()
        .get("pdf")
        .then((pdf: any) => {
          const total = pdf.internal.getNumberOfPages();
          for (let i = 1; i <= total; i++) {
            pdf.setPage(i);
            if (header) pdf.addImage(header.img, "JPEG", header.xMm, EDGE, header.wMm, header.hMm);
            if (footer) pdf.addImage(footer.img, "JPEG", footer.xMm, PAGE_H - EDGE - footer.hMm, footer.wMm, footer.hMm);
          }
        })
        .outputPdf("datauristring");

      const base64Separator = dataUri.indexOf(",");
      if (base64Separator < 0) throw new Error("Unable to create PDF attachment.");
      return dataUri.slice(base64Separator + 1);
    } finally {
      headerEls.forEach((element, index) => { element.style.display = previousHeaderDisplays[index]; });
      footerEls.forEach((element, index) => { element.style.display = previousFooterDisplays[index]; });
    }
  };

  const preparePdfAttachments = async (files: any[]) => {
    const preparedFiles: any[] = [];
    for (const file of files) {
      if (file.fileType === "Prescription") {
        const params = new URLSearchParams({
          reportName: file.reportName,
          reportType: "pdf",
          opdId: String(file.opdId),
          consumerId: String(userContext?.consumerId || 0),
          branchId: String(userContext?.branchId || 0),
          organizationId: String(userContext?.organizationId || 0),
          departmentId: String(file.departmentId || 0),
          specialityId: String(file.specialityId || 0),
          patientId: String(file.patientId || 0),
        });
        const response: any = await GetOpdDetailsForReport(params);
        const reportData = response?.data?.data;
        const hasPrescriptionData = reportData && (
          reportData.patientDetails?.length > 0 ||
          reportData.prescriptionDetails?.length > 0 ||
          reportData.prescriptionTable?.length > 0 ||
          reportData.opdDetails
        );
        if (!hasPrescriptionData) continue;
        setIsFullPrescriptionPdf(false);
        setPrescriptionPdfData(reportData);
        await waitForPdfContent();
        file.fileBase64 = await createPdfBase64(prescriptionPdfRef.current);
        preparedFiles.push(file);
        continue;
      }

      if (file.fileType === "FullPrescription") {
        const shortCode = opd?.fullPrescriptionToken;
        if (!shortCode) continue;

        // OPD links use /r?t=<shortCode>; resolve it like ShortLinkRedirect does.
        let fullPrescriptionToken = shortCode;
        try {
          const shortLinkParams = new URLSearchParams({ shortCode });
          const shortLinkResult: any = await GetRedirectLink(shortLinkParams);
          fullPrescriptionToken = shortLinkResult?.data?.data?.encryptedToken || shortCode;
        } catch {
          // Keep compatibility with records that already contain the encrypted token.
        }

        const tokenParams = new URLSearchParams({ token: fullPrescriptionToken });
        const tokenResult: any = await GetFullPrescriptionTokenDetail(tokenParams, fullPrescriptionToken);
        const tokenData = tokenResult?.data?.data;
        if (!tokenData?.opdId) continue;

        const today = new Date().toISOString().slice(0, 10);
        const startDateValue = tokenData.dateOfBirth || opd?.dateOfBirth || today;
        const startDate = String(startDateValue).slice(0, 10);
        const reportParams = new URLSearchParams({
          opdId: String(tokenData.opdId),
          consumerId: String(tokenData.consumerId || userContext?.consumerId || 0),
          organizationId: String(tokenData.organizationId || userContext?.organizationId || 0),
          branchId: String(tokenData.branchId || userContext?.branchId || 0),
          departmentId: String(tokenData.departmentId || opd?.departmentId || 0),
          specialityId: String(tokenData.specialityId || opd?.specialityId || 0),
          patientId: String(tokenData.patientId || opd?.patientId || 0),
          startDate,
          endDate: today,
        });
        const response: any = await GetFullPrescriptionReportDetail(reportParams, fullPrescriptionToken);
        const reportData = response?.data?.data;
        if (!reportData?.patientDetails?.length && !reportData?.prescriptionDetails?.length) continue;
        setIsFullPrescriptionPdf(true);
        setPrescriptionPdfData(reportData);
        await waitForPdfContent();
        file.fileBase64 = await createPdfBase64(prescriptionPdfRef.current);
        preparedFiles.push(file);
        continue;
      }

      if (file.fileType === "GrowthChart") {
        const formatDateParam = (value: any) => {
          const date = new Date(value);
          if (Number.isNaN(date.getTime())) return String(value || "").slice(0, 10);
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
        };
        const today = formatDateParam(new Date());
        const startDate = formatDateParam(opd?.patientBirthDate || opd?.dateOfBirth || today);
        const chartParams = new URLSearchParams({
          patientId: String(file.patientId || 0),
          familyId: String(opd?.familyId || 0),
          familyMemberId: String(opd?.familyMemberId || 0),
          startDate,
          endDate: today,
        });
        let chartData: any = null;
        try {
          const chartResult: any = await GetGrowthChart(chartParams);
          chartData = chartResult?.data?.data;
        } catch {
          chartData = null;
        }

        let fullPrescriptionData: any = null;
        const shortCode = opd?.fullPrescriptionToken;
        if (shortCode) {
          try {
            let fullPrescriptionToken = shortCode;
            try {
              const shortLinkParams = new URLSearchParams({ shortCode });
              const shortLinkResult: any = await GetRedirectLink(shortLinkParams);
              fullPrescriptionToken = shortLinkResult?.data?.data?.encryptedToken || shortCode;
            } catch {
              // Keep compatibility with records that already contain the encrypted token.
            }

            const tokenParams = new URLSearchParams({ token: fullPrescriptionToken });
            const tokenResult: any = await GetFullPrescriptionTokenDetail(tokenParams, fullPrescriptionToken);
            const tokenData = tokenResult?.data?.data;
            if (tokenData?.opdId) {
              const reportParams = new URLSearchParams({
                opdId: String(tokenData.opdId),
                consumerId: String(tokenData.consumerId || userContext?.consumerId || 0),
                organizationId: String(tokenData.organizationId || userContext?.organizationId || 0),
                branchId: String(tokenData.branchId || userContext?.branchId || 0),
                departmentId: String(tokenData.departmentId || opd?.departmentId || 0),
                specialityId: String(tokenData.specialityId || opd?.specialityId || 0),
                patientId: String(tokenData.patientId || file.patientId || 0),
                startDate: formatDateParam(tokenData.dateOfBirth || opd?.patientBirthDate || today),
                endDate: today,
              });
              const fullReportResult: any = await GetFullPrescriptionReportDetail(reportParams, fullPrescriptionToken);
              fullPrescriptionData = fullReportResult?.data?.data;
            }
          } catch {
            fullPrescriptionData = null;
          }
        }

        let reportData = fullPrescriptionData || opd;
        if (!fullPrescriptionData) {
          const reportParams = new URLSearchParams({
            opdId: String(file.opdId),
            consumerId: String(userContext?.consumerId || 0),
            branchId: String(userContext?.branchId || 0),
            organizationId: String(userContext?.organizationId || 0),
            departmentId: String(file.departmentId || 0),
            specialityId: String(file.specialityId || 0),
            patientId: String(file.patientId || 0),
          });
          try {
            const reportResult: any = await GetOpdDetailsForReport(reportParams);
            reportData = reportResult?.data?.data || opd;
          } catch {
            reportData = opd;
          }
        }

        if (fullPrescriptionData?.growthChartSeriesColl?.length) {
          chartData = fullPrescriptionData;
        } else if (reportData?.growthChartSeriesColl?.length) {
          chartData = reportData;
        }
        if (getGrowthCharts(chartData).length === 0) continue;

        setGrowthChartPdfData({ chartData, reportData });
        await waitForPdfContent();
        preparedFiles.push({
          ...file,
          fileBase64: await createPdfBase64(growthChartPdfRef.current),
        });
        continue;
      }
      if (file.fileType === "ReportCertificate") {
        const investigationParams = new URLSearchParams({
          opdId: String(file.opdId),
          consumerId: String(userContext?.consumerId || 0),
          orgId: String(userContext?.organizationId || 0),
          branchId: String(userContext?.branchId || 0),
          departmentId: String(file.departmentId || 0),
          specialityId: String(file.specialityId || 0),
          patientId: String(file.patientId || 0),
          sortColumn: "",
          sortDirection: "",
          searchValue: "",
        });
        let investigation: any = null;
        try {
          const investigationResult: any = await GetOPDInvestigationDetailById(investigationParams);
          investigation = investigationResult?.data?.data;
        } catch {
          investigation = null;
        }

        const certificateRecords = [
          ...(investigation?.reportCertificateColl || []),
          ...(investigation?.opdInvestigationDetailColl || []).flatMap(
            (detail: any) => detail?.reportCertificateDetailColl || [],
          ),
        ];
        const descriptions = Array.from(new Set(
          [file.description, ...certificateRecords.map((item: any) => item?.description)]
            .map((value) => String(value || "").trim())
            .filter(Boolean),
        ));

        const certificateReports: any[] = [];
        for (const description of descriptions) {
          try {
            const response: any = await GetPatientReportCertificatePreview({
              consumerId: userContext?.consumerId || 0,
              branchId: userContext?.branchId || 0,
              organizationId: userContext?.organizationId || 0,
              patientId: file.patientId,
              opdId: file.opdId,
              description,
            });
            if (response?.data?.previewDescription) certificateReports.push(response.data);
          } catch {
            
          }
        }
        if (certificateReports.length) {
          setCertificatePdfData(certificateReports);
          await waitForPdfContent();
          preparedFiles.push({
            ...file,
            pdfFileName: `Certificate_${opd?.caseNumber ?? "OPD"}.pdf`,
            fileBase64: await createPdfBase64(certificatePdfRef.current),
          });
        }
        continue;
      }

      preparedFiles.push(file);
    }
    return preparedFiles;
  };
  // Convert checked template links into PDF descriptors for the multi-file endpoint.
  // Normalize labels because DB display names may use different separators/casing.
  const buildFiles = () => {
    const baseName = opd?.caseNumber ?? "OPD";
    const files: any[] = [];
    const addOnce = (file: any) => {
      if (!files.some((item) => item.fileType === file.fileType)) files.push(file);
    };

    selectedLinks.forEach((link) => {
      const key = link.toUpperCase().replace(/[^A-Z0-9]/g, "");
      const common = {
        patientId: opd?.patientId ?? 0,
        opdId: opd?.opdId ?? 0,
        billingId: 0,
        description: "",
        reportType: "pdf",
      };

      if (key.includes("FULLPRESCRIPTION")) {
        addOnce({
          ...common,
          fileType: "FullPrescription",
          reportName: "FullPrescription",
          pdfFileName: `FullPrescription_${baseName}.pdf`,
          departmentId: opd?.departmentId ?? 0,
          specialityId: opd?.specialityId ?? 0,
        });
      } else if (key.includes("PRESCRIPTION")) {
        addOnce({
          ...common,
          fileType: "Prescription",
          reportName: userContext?.defaultPrescriptionTemplate || "Prescription",
          pdfFileName: `Prescription_${baseName}.pdf`,
          departmentId: opd?.departmentId ?? 0,
          specialityId: opd?.specialityId ?? 0,
        });
      } else if (key.includes("GROWTHCHART")) {
        addOnce({
          ...common,
          fileType: "GrowthChart",
          reportName: "GrowthChart",
          pdfFileName: `GrowthChart_${baseName}.pdf`,
          departmentId: opd?.departmentId ?? 0,
          specialityId: opd?.specialityId ?? 0,
        });
      } else if (key.includes("REPORT_CERTIFICATE") || key.includes("CERTIFICATE")) {
        addOnce({
          ...common,
          fileType: "ReportCertificate",
          reportName: "ReportCertificate",
          pdfFileName: `Certificate_${baseName}.pdf`,
          departmentId: opd?.departmentId ?? 0,
          specialityId: opd?.specialityId ?? 0,
        });
      } else if (key.includes("BILL")) {
        if (opd?.billingId) {
          addOnce({
            ...common,
            fileType: "Billing",
            reportName: "BillingInvoiceA4",
            pdfFileName: `Billing_${baseName}.pdf`,
            billingId: opd.billingId,
          });
        }
      }
    });

    return files;
  };

  // ON SUBMIT OF FORM
  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const files = await preparePdfAttachments(buildFiles());
      const formData = {
        opdId: opd?.opdId,
        patientId: opd?.patientId,
        consumerId: userContext?.consumerId,
        organizationId: userContext?.organizationId,
        branchId: userContext?.branchId,
        toEmail: values.to,
        ccEmail: values.cc || "",
        reportName: userContext?.defaultPrescriptionTemplate || "Prescription",
        reportType: "pdf",
        pdfFileName: `${opd?.caseNumber ?? "OPD"}.pdf`,
        page: "OPD",
        userName: opd?.patientName ?? "",
        companyName: userContext?.organizationName ?? "",
        branchName: userContext?.branchName ?? "",
        subjectEmail: "",
        templateLinks: selectedLinks.join(","),
        dataManagedBy: userContext?.loginDetail?.userId,
        templateBody: templatePreview || "",
        files,
      };
      const result: any = await SendMultipleFilesToEmail(formData);
      if (result) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
        onClose();
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message,
        alertType: AlertType.Error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendToChange = (selected: any) => {
    setSendTo(selected);
    const email = selected?.email || "";
    setValues((prev) => ({ ...prev, to: email }));
    methods.setValue("to", email);
    setTemplatePreview("");
    const templates = selected?.value === "referral" ? referralTemplates : selected?.value === "outreach" ? outreachTemplates : patientTemplates;
    const def = templates.find((t: any) => t.isDefault);
    setSelectedTemplateId(def ? def.notificationTemplateId : "");
  };

  const fetchEmailTemplates = async () => {
    try {
      const params = (templateFor: string) =>
        new URLSearchParams({
          consumerId: userContext.consumerId.toString(),
          organizationId: userContext.organizationId.toString(),
          branchId: userContext.branchId.toString(),
          mediaType: "EMAIL",
          pageIndex: "1",
          pageSize: "100",
          mode: "LIST",
          templateFor,
        });
      const [res1, res2, res3]: any = await Promise.all([
        GetNotificationTemplateList(params("PATIENT_OPD")),
        GetNotificationTemplateList(params("REFERRAL_DOCTOR_OPD")),
        GetNotificationTemplateList(params("OUT_REACH")),
      ]);
      const pt = res1?.data?.data?.notificationTemplateColl || [];
      const rt = res2?.data?.data?.notificationTemplateColl || [];
      const ot = res3?.data?.data?.notificationTemplateColl || [];
      setPatientTemplates(pt);
      setReferralTemplates(rt);
      setOutreachTemplates(ot);
      const defPt = pt.find((t: any) => t.isDefault);
      const defRt = rt.find((t: any) => t.isDefault);
      const defOt = ot.find((t: any) => t.isDefault);
      if (defPt) {
        setDefaultPatientTemplateId(defPt.notificationTemplateId);
        setSelectedTemplateId(defPt.notificationTemplateId);
      }
      if (defRt) setDefaultReferralTemplateId(defRt.notificationTemplateId);
      if (defOt) setDefaultOutreachTemplateId(defOt.notificationTemplateId);
    } catch (e) { }
  };

  const handleSetAsDefault = async () => {
    if (!selectedTemplateId) return;
    const templateFor = sendTo?.value === "referral" ? "REFERRAL_DOCTOR_OPD" : sendTo?.value === "outreach" ? "OUT_REACH" : "PATIENT_OPD";
    try {
      await UpdateDefaultNotificationTemplate({
        notificationTemplateId: selectedTemplateId.toString(),
        consumerId: userContext.consumerId.toString(),
        organizationId: userContext.organizationId.toString(),
        branchId: userContext.branchId.toString(),
        mediaType: "EMAIL",
        templateFor,
      });
      if (sendTo?.value === "referral") {
        setDefaultReferralTemplateId(Number(selectedTemplateId));
        setReferralTemplates((prev) => prev.map((t) => ({ ...t, isDefault: t.notificationTemplateId === selectedTemplateId })));
      } else if (sendTo?.value === "outreach") {
        setDefaultOutreachTemplateId(Number(selectedTemplateId));
        setOutreachTemplates((prev) => prev.map((t) => ({ ...t, isDefault: t.notificationTemplateId === selectedTemplateId })));
      } else {
        setDefaultPatientTemplateId(Number(selectedTemplateId));
        setPatientTemplates((prev) => prev.map((t) => ({ ...t, isDefault: t.notificationTemplateId === selectedTemplateId })));
      }
    } catch (e) { }
  };

  const fetchTemplatePreview = async (templateData: any) => {
    try {
      const res: any = await GetOpdMessagePreview({
        consumerId: userContext.consumerId,
        branchId: userContext.branchId,
        orgId: userContext.organizationId,
        patientId: opd?.patientId,
        opdId: opd?.opdId || 0,
        envUrl: `${UI_APP_URL}/r?t=`,
        shortToken: opd?.token || "",
        description: templateData,
      });
      setTemplatePreview(res?.data?.previewDescription || "");
    } catch (e) {
      return "";
    }
  };

  const handleLinkToggle = (link: string) => {
    setSelectedLinks((prev) =>
      prev.includes(link)
        ? prev.filter((item) => item !== link)
        : [...prev, link]
    );
  };

  // Auto-populate patient email by default
  useEffect(() => {
    const defaultOption = sendToOptions[0];
    setSendTo(defaultOption);
    const email = defaultOption?.email || "";
    setValues((prev) => ({ ...prev, to: email }));
    methods.setValue("to", email);
  }, [opd]);

  useEffect(() => {
    fetchEmailTemplates();
  }, []);

  useEffect(() => {
    if (!selectedTemplateId) {
      setTemplatePreview("");
      setSelectedLinks([]);
      return;
    }

    const allTemplates = [
      ...patientTemplates,
      ...referralTemplates,
      ...outreachTemplates,
    ];

    const template = allTemplates.find(
      (t) =>
        Number(t.notificationTemplateId) === Number(selectedTemplateId)
    );

    if (template) {
      fetchTemplatePreview(template.templateData);

      setSelectedLinks(
        template.templateLinks
          ? template.templateLinks
            .split(",")
            .map((l: string) => l.trim())
            .filter((l: string) =>
              availableLinks.some((link) => link.value === l)
            )
          : []
      );
    }
  }, [
    selectedTemplateId,
    opd,
    patientTemplates,
    referralTemplates,
    outreachTemplates,
  ]);

  return (
    <Box className={"main-container"}>
      <Box aria-hidden="true" sx={{ position: "fixed", left: "-10000px", top: 0, width: "794px", backgroundColor: "#fff", zIndex: -1 }}>
        <Box ref={prescriptionPdfRef} sx={{ width: isFullPrescriptionPdf ? "794px" : "190mm", maxWidth: isFullPrescriptionPdf ? "794px" : "190mm", backgroundColor: "#fff" }}>
          {prescriptionPdfData && (
            isFullPrescriptionPdf ? (
              <FullPrescriptionReportEmailContent reportData={prescriptionPdfData} />
            ) : (
              <PrescriptionReportEmailContent
                reportData={prescriptionPdfData}
                selectedSections={getReportSections("REPORT_CONFIG")}
              />
            )
          )}
        </Box>
        <Box ref={growthChartPdfRef} sx={{ width: "190mm", maxWidth: "190mm", backgroundColor: "#fff" }}>
          {growthChartPdfData && <GrowthChartEmailContent chartData={growthChartPdfData.chartData} reportData={growthChartPdfData.reportData} />}
        </Box>
        <Box ref={certificatePdfRef} sx={{ width: "190mm", maxWidth: "190mm", boxSizing: "border-box", backgroundColor: "#fff" }}>
          {certificatePdfData?.map((reportData: any, index: number) => (
            <div
              key={`${reportData?.description || "certificate"}-${index}`}
              className={index > 0 ? "certificate-pdf-page" : undefined}
            >
              <div className="pdf-repeat-header">
                <ReportCertificateHeader reportData={enablePdfHeaderFooter(reportData)} selectedSections={{ ...getReportSections("CERTIFICATE_REPORT_CONFIG"), HEADER: true, FOOTER: true }} />
              </div>
              <div style={{ margin: "10px 16px" }} dangerouslySetInnerHTML={{ __html: reportData.previewDescription }} />
              <div className="pdf-repeat-footer">
                <ReportCertificateFooter reportData={enablePdfHeaderFooter(reportData)} selectedSections={{ ...getReportSections("CERTIFICATE_REPORT_CONFIG"), HEADER: true, FOOTER: true }} />
              </div>
            </div>
          ))}
        </Box>
      </Box>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} action="">
          <Grid container gap={2}>
            <Grid item xs={12} mb={2}>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12}>
                  <CustomSelectWithoutForm
                    id="sendTo"
                    FieldName="sendTo"
                    placeholder="Select recipient"
                    options={sendToOptions}
                    value={sendTo}
                    handleChange={handleSendToChange}
                    isNotCreatable
                    isClearable={false}
                    isSearchable={false}
                    menuPortalTarget={document.querySelector("body")}
                  />
                </Grid>
                {(sendTo?.value === "patient" || sendTo?.value === "referral" || sendTo?.value === "outreach") &&
                  (sendTo?.value === "referral" ? referralTemplates : sendTo?.value === "outreach" ? outreachTemplates : patientTemplates).length > 0 && (
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <FormLabel className="fileds-label-text">Email Template</FormLabel>
                        <Select
                          value={selectedTemplateId}
                          onChange={(e) => setSelectedTemplateId(Number(e.target.value))}
                          displayEmpty
                          size="small"
                        >
                          {(sendTo?.value === "referral" ? referralTemplates : sendTo?.value === "outreach" ? outreachTemplates : patientTemplates).map((t) => (
                            <MenuItem key={t.notificationTemplateId} value={t.notificationTemplateId}>
                              {t.templateName}{t.isDefault ? " (Default)" : ""}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {selectedTemplateId !== "" &&
                        selectedTemplateId !== (sendTo?.value === "referral" ? defaultReferralTemplateId : sendTo?.value === "outreach" ? defaultOutreachTemplateId : defaultPatientTemplateId) && (
                          <LoadingButton
                            onClick={handleSetAsDefault}
                            variant="outlined"
                            color="primary"
                            size="small"
                            sx={{ mt: 1 }}
                          >
                            Set as Default
                          </LoadingButton>
                        )}
                      {templatePreview && (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1.5,
                            border: "1px solid #ccc",
                            borderRadius: 1,
                            backgroundColor: "#f9f9f9",
                            fontSize: "13px",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          <Typography variant="body2">
                            {parse(templatePreview)}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  )}

                <Grid item xs={12}>
                  <Typography sx={{ color: "#0009", marginBottom: "5px" }}>
                    Attachments:
                  </Typography>
                  <Box
                    className="main-dialog-background-color"
                    sx={{
                      padding: 2,
                      display: "flex",
                      flexWrap: "wrap",
                      overflow: "hidden",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                      borderRadius: "8px",
                    }}
                  >
                    <Grid container spacing={2} wrap="wrap">
                      {availableLinks?.map((link) => (
                        <Grid key={link?.value} item xs={12} md={6}>
                          <FormControlLabel
                            sx={{ margin: 0, width: "100%" }}
                            control={
                              <Checkbox
                                size="small"
                                sx={{ padding: "0 8px 0 0" }}
                                checked={selectedLinks.includes(link?.value)}
                                onChange={() => handleLinkToggle(link?.value)}
                              />
                            }
                            label={
                              <Typography sx={{ fontSize: "inherit" }}>
                                {link?.label}
                              </Typography>
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <FormLabel
                      className={`fileds-label-text  ${methods.formState.errors.to
                        ? "validation-message"
                        : ""
                        }`}
                    >
                      To
                    </FormLabel>
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-input"
                    >
                      <CustomTextField
                        fullWidth
                        placeholder="Enter email address"
                        size="small"
                        value={values.to}
                        onChange={(e: any) => {
                          setValues({ ...values, to: e.target.value });
                          methods.setValue("to", e.target.value);
                        }}
                      />
                    </SkeletonWrapper>
                    {!showCC && (
                      <Box
                        component="span"
                        onClick={() => setShowCC(true)}
                        sx={{
                          position: "absolute",
                          right: 12,
                          top: "70%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          color: "text.secondary",
                          fontSize: "13px",
                          userSelect: "none",
                          "&:hover": {
                            color: "primary.main",
                          },
                        }}
                      >
                        CC
                      </Box>
                    )}
                  </FormControl>
                  <Typography className="validation-message">
                    {methods.formState.errors.to?.message}
                  </Typography>
                </Grid>
                {/* CC */}
                {showCC && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.cc
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        CC
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          fullWidth
                          placeholder="Enter email address"
                          size="small"
                          value={values.cc}
                          onChange={(e: any) => {
                            setValues({ ...values, cc: e.target.value });
                            methods.setValue("cc", e.target.value);
                          }}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.cc?.message}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                <LoadingButton
                  color="primary"
                  loadingPosition="start"
                  startIcon={<></>}
                  variant="contained"
                  type="submit"
                  className="primary-button"
                  name="save"
                  loading={isLoading}
                  disabled={isLoading || methods.formState.isSubmitting}
                >
                  Send
                </LoadingButton>
                <LoadingButton
                  color="primary"
                  loadingPosition="start"
                  startIcon={<></>}
                  variant="outlined"
                  type="button"
                  className="secondary-button"
                  name="cancel"
                  disabled={isLoading || methods.formState.isSubmitting}
                  onClick={onClose}
                >
                  Cancel
                </LoadingButton>
              </Box>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Box >
  );
};

export default EmailSendDialogForOPD;
